import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { Badge, Card, Container, Overline, Skeleton } from '@/design-system/primitives'
import { ApiError } from '@/lib/apiClient'
import { cn } from '@/lib/cn'

import { useJurySheet, useJuryScore } from '../api'
import type { JuryCandidate, JuryRound } from '../types'

/**
 * La page de notation d'un juré — `/jury?token=…&juror_id=…`.
 *
 * Tenue d'une main, dans une salle sombre, pendant qu'un candidat défile :
 * dix grosses touches par candidat, la note part au premier appui, et ce qui
 * est enregistré se voit sans ambiguïté. Pas de bouton « valider » à oublier.
 *
 * La note se donne et se corrige tant que le vote du tour est ouvert ; gelé,
 * le tour se fige et la page le dit. Le juré ne voit que SES notes — ni
 * celles des autres jurés, ni les voix du public : le serveur ne les envoie
 * pas.
 */
export function JuryPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const jurorId = searchParams.get('juror_id')
  const sheet = useJurySheet(token, jurorId)

  useEffect(() => {
    document.title = t('jury.title')
  }, [t])

  if (!token || !jurorId || sheet.isError) {
    return (
      <Container className="py-24">
        <h1 className="font-display text-display-md text-content-primary">
          {t('jury.invalidTitle')}
        </h1>
        <p className="mt-4 max-w-lg text-content-secondary">{t('jury.invalidHelp')}</p>
      </Container>
    )
  }

  if (sheet.isPending) {
    return (
      <Container className="py-16">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-8 h-64 w-full" />
      </Container>
    )
  }

  const data = sheet.data
  const open = data.rounds.filter((round) => round.editable)

  return (
    <Container className="py-10 sm:py-16">
      <Overline>{t('jury.overline', { contest: data.contest })}</Overline>
      <h1 className="mt-2 font-display text-display-md text-content-primary">
        {data.juror_name}
      </h1>
      <p className="mt-3 max-w-xl text-sm text-content-secondary">
        {t('jury.help', { min: data.min_score, max: data.max_score })}
      </p>

      {open.length === 0 ? (
        <Card className="mt-8">
          <p className="text-content-secondary">{t('jury.nothingOpen')}</p>
        </Card>
      ) : null}

      <div className="mt-8 space-y-6">
        {data.rounds.map((round) => (
          <RoundCard
            key={round.vote_category_id}
            round={round}
            token={token}
            jurorId={jurorId}
            min={data.min_score}
            max={data.max_score}
          />
        ))}
      </div>

      <p className="mt-10 text-caption text-content-muted">{t('jury.privateNote')}</p>
    </Container>
  )
}

function RoundCard({
  round,
  token,
  jurorId,
  min,
  max,
}: {
  round: JuryRound
  token: string
  jurorId: string
  min: number
  max: number
}) {
  const { t } = useTranslation()
  const given = round.candidates.filter((candidate) => candidate.score !== null).length

  return (
    <Card className={cn('space-y-4', round.editable && 'border-2 border-ember-400')}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-heading-md text-content-primary">{round.round_name}</h2>
        <Badge tone={round.editable ? 'ember' : 'neutral'}>
          {round.editable ? t('jury.open') : t('jury.locked')}
        </Badge>
      </div>
      <p className="text-caption text-content-muted">
        {t('jury.progress', { given, total: round.candidates.length })}
      </p>
      <ul className="space-y-5">
        {round.candidates.map((candidate) => (
          <li key={candidate.candidate_id}>
            <CandidateScore
              candidate={candidate}
              roundId={round.vote_category_id}
              editable={round.editable}
              token={token}
              jurorId={jurorId}
              min={min}
              max={max}
            />
          </li>
        ))}
      </ul>
    </Card>
  )
}

function CandidateScore({
  candidate,
  roundId,
  editable,
  token,
  jurorId,
  min,
  max,
}: {
  candidate: JuryCandidate
  roundId: string
  editable: boolean
  token: string
  jurorId: string
  min: number
  max: number
}) {
  const { t, i18n } = useTranslation()
  const save = useJuryScore(token, jurorId)
  // La note tapée s'affiche tout de suite ; le serveur la confirme ensuite.
  const [pending, setPending] = useState<number | null>(null)
  const shown = pending ?? candidate.score
  const scale = Array.from({ length: max - min + 1 }, (_, index) => min + index)

  function explain(cause: unknown): string {
    if (cause instanceof ApiError) {
      const key = `jury.error.${cause.code}`
      return i18n.exists(key) ? t(key) : cause.message
    }
    return t('jury.error.network')
  }

  return (
    <div>
      <div className="flex items-center gap-3">
        {candidate.photo_url ? (
          <img
            src={candidate.photo_url}
            alt=""
            className="h-12 w-12 shrink-0 rounded-full object-cover"
            loading="lazy"
          />
        ) : null}
        <div className="min-w-0 flex-1">
          <p className="truncate text-content-primary">
            {candidate.candidate_number !== null ? (
              <span className="mr-2 font-mono text-champagne-300">
                #{candidate.candidate_number}
              </span>
            ) : null}
            {candidate.name}
          </p>
          <p className="text-caption text-content-muted">
            {shown !== null ? t('jury.yourScore', { score: shown }) : t('jury.notScored')}
          </p>
        </div>
      </div>

      {/* Dix cibles larges, cinq par ligne : on vise avec le pouce. */}
      <div className="mt-3 grid grid-cols-5 gap-2" role="group" aria-label={candidate.name}>
        {scale.map((value) => (
          <button
            key={value}
            type="button"
            disabled={!editable || save.isPending}
            aria-pressed={shown === value}
            onClick={() => {
              setPending(value)
              save.mutate(
                { roundId, candidateId: candidate.candidate_id, score: value },
                { onSettled: () => setPending(null) },
              )
            }}
            className={cn(
              'h-12 rounded-md border font-mono text-lg tabular-nums transition',
              shown === value
                ? 'border-champagne-400 bg-champagne-400/15 text-champagne-300'
                : 'border-line-subtle text-content-secondary',
              editable && 'hover:border-champagne-400/60 hover:text-content-primary',
              !editable && 'opacity-50',
            )}
          >
            {value}
          </button>
        ))}
      </div>
      {save.isError ? (
        <p className="mt-2 text-caption text-state-danger">{explain(save.error)}</p>
      ) : null}
    </div>
  )
}
